import { showSuccessAlert } from 'actions';
import { addNewPromptBlock } from 'actions/identifier';
import {
  deletePromptTemplate,
  deletePromptTemplateFailure,
  deletePromptTemplateRequest,
  deletePromptTemplateSuccess,
  fetchPromptTemplates,
  fetchPromptTemplatesFailure,
  fetchPromptTemplatesFulfill,
  fetchPromptTemplatesRequest,
  fetchPromptTemplatesSuccess,
  fetchSinglePromptTemplate,
  fetchSinglePromptTemplateFailure,
  fetchSinglePromptTemplateRequest,
  fetchSinglePromptTemplateSuccess,
  upsertAndAddPromptTemplate,
  upsertPromptTemplate,
  upsertPromptTemplateFailure,
  upsertPromptTemplateRequest,
  upsertPromptTemplateSuccess,
} from 'actions/promptTemplates';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain } from 'selectors/auth';
import { getNavParamsByResource } from 'selectors/nav';
import { API, SagaReturn } from 'types';
import { reverse } from 'utils/urls';

function* fetchSinglePromptTemplateSaga(
  action: ReturnType<typeof fetchSinglePromptTemplate>
): SagaReturn {
  const { payload } = action;
  yield put(fetchSinglePromptTemplateRequest());
  const response = (yield call(
    [LitLingoClient.resources.llm_prompt_templates, 'retrieve'],
    payload.promptTemplateId
  )) as API.Response<API.LLMPromptTemplates.Retrieve>;
  if (response.error != null) {
    yield put(fetchSinglePromptTemplateFailure(response.error));
  } else {
    yield put(fetchSinglePromptTemplateSuccess(response.data));
  }
}

function* fetchPromptTemplatesSaga(action: ReturnType<typeof fetchPromptTemplates>): SagaReturn {
  const payloadParams = action?.payload || {};

  yield put(fetchPromptTemplatesRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.llm_prompt_templates)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const params: { include_count: boolean; kind?: string } = {
    include_count: true,
    ...resourceParams,
    ...payloadParams,
  };

  const response = (yield call([LitLingoClient.resources.llm_prompt_templates, 'list'], {
    params,
  })) as API.Response<API.LLMPromptTemplates.List>;

  if (response.error != null) {
    yield put(fetchPromptTemplatesFailure(response.error));
  } else {
    yield put(fetchPromptTemplatesSuccess(response.data));
  }

  yield put(fetchPromptTemplatesFulfill());
}

function* upsertPromptTemplateSaga(action: ReturnType<typeof upsertPromptTemplate>): SagaReturn {
  const { payload } = action;
  const { ...rest } = payload;
  const params = {};

  yield put(upsertPromptTemplateRequest());

  const response = (yield call([LitLingoClient.resources.llm_prompt_templates, 'upsert'], {
    params,
    data: rest,
  })) as API.Response<API.LLMPromptTemplates.Upsert>;

  if (response.error != null) {
    yield put(upsertPromptTemplateFailure(response.error));

    yield put(
      showSuccessAlert(
        "Something went wrong and the new prompt template can't be saved at this time"
      )
    );
  } else {
    yield put(upsertPromptTemplateSuccess(response.data));
    if (!rest.uuid) {
      yield put(showSuccessAlert(`${rest.name} has been created successfully`));
    } else {
      yield put(showSuccessAlert(`${rest.name} has been saved`));
    }
  }
}

function* upsertAndAddPromptTemplateSaga(
  action: ReturnType<typeof upsertAndAddPromptTemplate>
): SagaReturn {
  const { payload } = action;

  yield put(upsertPromptTemplateRequest());

  const response = (yield call([LitLingoClient.resources.llm_prompt_templates, 'upsert'], {
    data: payload,
  })) as API.Response<API.LLMPromptTemplates.Upsert>;

  if (response.error != null) {
    yield put(upsertPromptTemplateFailure(response.error));

    yield put(
      showSuccessAlert(
        "Something went wrong and the new prompt template can't be saved at this time"
      )
    );
  } else {
    yield put(upsertPromptTemplateSuccess(response.data));
    yield put(addNewPromptBlock({ parent: payload.parent, template: response.data }));
    yield put(showSuccessAlert(`${response.data.name} has been created successfully`));
  }
}

function* deletePromptTemplateSaga(action: ReturnType<typeof deletePromptTemplate>): SagaReturn {
  const { uuid, redirect } = action.payload;

  yield put(deletePromptTemplateRequest());

  const response = (yield call(
    [LitLingoClient.resources.llm_prompt_templates, 'delete'],
    uuid
  )) as API.Response<API.LLMPromptTemplates.Upsert>;

  if (response.error != null) {
    yield put(deletePromptTemplateFailure(response.error));
  } else {
    yield put(deletePromptTemplateSuccess({ uuid }));
    yield put(showSuccessAlert('Prompt template deleted'));
    if (redirect) {
      const customerDomain =
        ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

      const path = reverse({
        routeName: 'prompts-templates',
        customerDomain,
      });
      yield put(push(path));
    }
  }
}

function* promptTemplatesSaga(): SagaReturn {
  yield takeLatest(fetchSinglePromptTemplate.toString(), fetchSinglePromptTemplateSaga);
  yield takeLatest(fetchPromptTemplates.toString(), fetchPromptTemplatesSaga);
  yield takeLatest(upsertPromptTemplate.toString(), upsertPromptTemplateSaga);
  yield takeLatest(upsertAndAddPromptTemplate.toString(), upsertAndAddPromptTemplateSaga);
  yield takeLatest(deletePromptTemplate.toString(), deletePromptTemplateSaga);
}

export default promptTemplatesSaga;
